import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, HostBinding, Input, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { IMenuItem } from '../../interfaces/layout.interface';
import { LayoutService } from '../../services/layout.service';

@Component({
    standalone: false,
    selector: '[app-admin-menuitem]',
    templateUrl: './admin-menuitem.component.html',
    styleUrl: './admin-menuitem.component.scss',
    animations: [
        trigger('children', [
            state('collapsed', style({ height: '0' })),
            state('expanded', style({ height: '*' })),
            transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
        ]),
    ],
})
export class AdminMenuitemComponent implements OnInit, OnDestroy {
    @Input({ required: true }) item!: IMenuItem;
    @Input({ required: true }) index!: number;
    @Input() parentKey = '';
    @Input() @HostBinding('class.layout-root-menuitem') root = false;

    protected readonly router = inject(Router);
    private readonly layoutService = inject(LayoutService);

    // A signal, so that opening or closing redraws the submenu along with its arrow.
    protected readonly active = signal(false);
    @HostBinding('class.active-menuitem') protected get isActive(): boolean {
        return this.active();
    }
    protected key = '';

    private readonly subscriptions: Subscription[] = [];

    constructor() {
        this.subscriptions.push(
            this.layoutService.menuSource$.subscribe((event) => {
                Promise.resolve(null).then(() => {
                    if (event.routeEvent) {
                        this.active.set(
                            event.key === this.key || event.key.startsWith(`${this.key}-`),
                        );
                        return;
                    }
                    if (event.key !== this.key && !event.key.startsWith(`${this.key}-`)) {
                        this.active.set(false);
                    }
                });
            }),

            this.router.events
                .pipe(filter((event) => event instanceof NavigationEnd))
                .subscribe(() => this.updateActiveStateFromRoute()),
        );
    }

    ngOnInit(): void {
        this.key = this.parentKey ? `${this.parentKey}-${this.index}` : String(this.index);
        this.updateActiveStateFromRoute();
    }

    protected itemClick(event: Event): void {
        if (this.item.disabled) {
            event.preventDefault();
            return;
        }

        this.item.command?.({ originalEvent: event, item: this.item });

        if (this.item.items) {
            this.active.update((active) => !active);
        } else {
            this.layoutService.hideMenu();
        }

        this.layoutService.onMenuStateChange({ key: this.key });
    }

    // Only a link can match the route; a group with no link of its own opens through its child.
    private updateActiveStateFromRoute(): void {
        const link = this.item.routerLink?.[0];
        if (!link) return;
        const active = this.router.isActive(link, {
            paths: 'exact',
            queryParams: 'ignored',
            matrixParams: 'ignored',
            fragment: 'ignored',
        });
        if (active) {
            this.layoutService.onMenuStateChange({ key: this.key, routeEvent: true });
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }
}
